import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Button, Icon, tokens } from '@hotjar/hotsauce';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useOwnedAccount } from '@hotjar/account';
import { useTranslation } from 'react-i18next';
import { translationDomain } from './common';
import { BillingNotificationsModal } from './BillingNotificationModal';
import type { EmailRecipients } from './api/emailRecipients';
import { getEmailRecipients, api } from './api/emailRecipients';

const StyledFieldset = styled.fieldset`
  margin: ${tokens.spacer.x3} 0;
`;

const StyledInfo = styled.div`
  margin: 0 0 ${tokens.spacer.x2};

  p {
    margin: 0;
    color: ${tokens.color.text_default};
    ${tokens.text('body2')}
  }
`;

const writeEmailRecipients = (emailRecipients: string[]): string => {
  let textOutput = '';
  if (emailRecipients) {
    emailRecipients.forEach((email, index, emails) => {
      let separator = '';
      if (index !== 0) {
        separator = index === emails.length - 1 ? ' and ' : ', ';
      }
      textOutput += separator + email;
    });
  }
  return textOutput;
};

export const BillingNotifications = (): JSX.Element => {
  const { data: account } = useOwnedAccount();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [emailRecipients, setEmailRecipients] = useState<EmailRecipients>([]);
  const { success, error } = useNotify();
  const { t } = useTranslation();

  useEffect(() => {
    if (account?.id === undefined) {
      return;
    }
    const fetchData = async () => {
      const response = await getEmailRecipients(account.id);
      if (response.success) {
        setEmailRecipients(response.emailRecipients);
      } else {
        error(t(`${translationDomain}.data-fetch-problem`));
      }
    };

    fetchData();
  }, [account, error, t]);

  const persistEmailRecipients = async (newEmailRecipients: EmailRecipients) => {
    if (account?.id === undefined) {
      return;
    }
    const response = await api.persistEmailRecipients(account.id, newEmailRecipients);
    if (response.success) {
      setEmailRecipients(newEmailRecipients);
      success(t(`${translationDomain}.save-success`));
    } else {
      error(t(`${translationDomain}.save-error`));
    }
  };

  const hasEmails = emailRecipients?.length > 0;

  return (
    <StyledFieldset>
      <legend>{t(`${translationDomain}.title`)}</legend>

      <>
        <StyledInfo>
          <p>
            {hasEmails ? (
              'Billing notifications are sent to:'
            ) : (
              <>
                <Icon icon="warning_alt" color="text_error" size="m" />{' '}
                {t(`${translationDomain}.no-notifications-are-sent`)}
              </>
            )}
          </p>
          <>{hasEmails && writeEmailRecipients(emailRecipients)}</>
        </StyledInfo>
        <Button size="s" onClick={() => setIsOpen(!isOpen)}>
          {hasEmails
            ? t(`${translationDomain}.button-update-email`)
            : t(`${translationDomain}.button-set-email`)}
        </Button>
      </>
      <BillingNotificationsModal
        isOpen={isOpen}
        close={() => setIsOpen(false)}
        currentEmailRecipients={emailRecipients}
        persistEmailRecipients={persistEmailRecipients}
      />
    </StyledFieldset>
  );
};
